<script lang="ts" setup>
import MCardItem from '~/components/molecules/CardItem.vue'
import MImageLoader from '@/components/molecules/forms/ImageLoader.vue'
import { Card } from '~/static/cards'
import { CardType } from '~/static/enums/card-type'
import AIconSquareDotted from '~/components/atoms/icons/SquareDotted.vue'
import MFormSearch from '~/components/molecules/forms/Search.vue'
import MDropdownMenu from '~/components/molecules/DropdownMenu.vue'
import { Dropdown as FDropdown } from 'floating-vue'
import { Directory } from '~/static/types/directories'

interface Props {
  type: CardType
  cards: Card[]
  totalCount: number
  itemWidth: number
  itemHeight: number
  directories: Directory[]
  selectedDirectory: number | null
  isLoading: boolean
  searchString: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'load'): void
  (e: 'load-image', file: File): void
  (e: 'clone', id: number): void
  (e: 'edit', id: number): void
  (e: 'delete', id: number): void
  (e: 'open', id: number): void
  (e: 'create-directory'): void
  (e: 'edit-directory', id: number): void
  (e: 'delete-directory', id: number): void
  (e: 'open-directory', id: number | null): void
  (e: 'search', text: string): void
  (e: 'put-to-directory', id: number): void
  (e: 'exclude-from-directory', id: number): void
}>()

const loadCards = (): void => {
  emit('load')
}

const openDirectory = (id: number | null): void => {
  emit('open-directory', id)
}

const localSearchString = ref(props.searchString)
const search = (): void => {
  emit('search', localSearchString.value)
}
const clearSearch = (): void => {
  localSearchString.value = ''
  emit('search', localSearchString.value)
}

onMounted(() => {
  document.querySelector('.wrapper')?.addEventListener('scroll', checkScroll)
  document.querySelector('.wrapper')?.addEventListener('resize', checkScroll)
})

onBeforeUnmount(() => {
  document.querySelector('.wrapper')?.removeEventListener('scroll', checkScroll)
  document.querySelector('.wrapper')?.removeEventListener('resize', checkScroll)
})

onUpdated(() => {
  checkScroll()
})

const checkScroll = () => {
  const { scrollTop, scrollHeight, clientHeight } = <Element>document.querySelector('.wrapper')
  if (scrollTop + clientHeight >= scrollHeight - 50) {
    loadCards()
  }
}

const openEditor = () => {
  useRouter().push('/file')
}

const onSelectImage = (file: File) => {
  emit('load-image', file)
}

const openCard = (id: number) => {
  emit('open', id)
}

const createDirectory = () => {
  emit('create-directory')
}

const editDirectory = (id?: number) => {
  if (id === undefined) {
    return
  }
  emit('edit-directory', id)
}

const deleteDirectory = (id?: number) => {
  if (id === undefined) {
    return
  }
  emit('delete-directory', id)
}

</script>

<template>
  <div
    v-if="type === CardType.ProductCard"
    class="tab-pane fade show active"
    id="nav-cards"
    role="tabpanel"
    aria-labelledby="nav-cards-tab"
  >
    <div class="row cards-wrapper">
      <div class="col-12 col-lg-2">
        <div class="aside-wrap">
          <div class="scrollbar aside">
            <button
              class="aside__item"
              :class="{ active: selectedDirectory === null }"
              @click.prevent="openDirectory(null)"
            >
              <span>Все карточки</span>
            </button>
            <button
              class="aside__item"
              @click.prevent="createDirectory"
            >
              <BootstrapIconFolderPlus class="icon-size-14" /> <span>Создать папку</span>
            </button>
            <div
              v-for="directory in directories"
              :key="directory.id"
              :class="{ active: selectedDirectory === directory.id }"
              class="aside__item"
            >
              <FDropdown
                placement="bottom-start"
                :distance="4"
                :delay="0.3"
                :skidding="-12"
              >
                <button class="button-icon item-icon">
                  <BootstrapIconThreeDots class="icon-size-14" />
                </button>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="[
                      { text: 'Переименовать', handler: editDirectory, handlerPayload: directory.id },
                      { text: 'Удалить', colorRed: true, handler: deleteDirectory, handlerPayload: directory.id },
                    ]"
                    theme="light-2"
                    position="start"
                    @click="hide"
                  />
                </template>
              </FDropdown>
              <span
                :title="directory.name"
                @click="openDirectory(directory.id)"
              >
                {{ directory.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-10">
        <div class="search-cards">
          <MFormSearch
            id="searchCards"
            placeholder="Поиск"
            :text="localSearchString"
            :search-started="searchString !== null && searchString.length > 0"
            :searched-text="searchString"
            :show-not-found="false"
            @change="localSearchString = $event"
            @search="search"
            @close-search="clearSearch"
          />
        </div>
        <div class="grid-cards-wrap my-3 my-md-4">
          <div class="grid-cards">
            <div class="card-btns-wrap">
              <MImageLoader
                v-slot="scope"
                @change="onSelectImage"
              >
                <div
                  class="card-btn"
                  @click="scope.loadImage"
                >
                  <BootstrapIconPlusCircle class="icon-size-22" />
                  Загрузить фото
                </div>
              </MImageLoader>
              <a
                href="/file"
                @click.prevent="openEditor"
              >
                <div class="card-btn">
                  <AIconSquareDotted class="icon-size-22" />
                  Открыть редактор
                </div>
              </a>
            </div>
            <div
              v-if="cards.length === 0 && searchString.length > 0 && !isLoading"
              class="not-results"
            >
              😔<br>
              По запросу «{{ searchString }}» карточки не найдены
            </div>
            <div
              v-else-if="isLoading && cards.length === 0"
              class="loading-card"
            >
              <div
                class="spinner-border"
                role="status"
              />
            </div>
            <MCardItem
              v-for="card in cards"
              :key="card.id"
              :card="card"
              :height="itemHeight"
              :width="itemWidth"
              :in-directory="card.directory_id !== null"
              :is-card="true"
              @open="openCard(card.id)"
              @clone="$emit('clone', card.id)"
              @change-name="$emit('edit', card.id)"
              @delete="$emit('delete', card.id)"
              @put-to-directory="$emit('put-to-directory', card.id)"
              @exclude-from-directory="$emit('exclude-from-directory', card.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="tab-pane fade mb-4 show active"
    id="nav-templates"
    role="tabpanel"
    aria-labelledby="nav-templates-tab"
  >
    <div class="row">
      <MCardItem
        v-for="card in cards"
        :key="card.id"
        class="col-6 col-md-3 col-xl-2 mt-4"
        :card="card"
        :height="itemHeight"
        :width="itemWidth"
        :in-directory="false"
        :is-card="false"
        @open="openCard(card.id)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$ratio-width: 166;
$ratio-height: 220;

.cards-wrapper {
  margin-top: rem(24px);
  
  @include media-breakpoint-down(lg) {
    margin-top: rem(20px);
  }

  @include media-breakpoint-down(md) {
    margin-top: rem(16px);
  }
}

.card-btns-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: rem(12px);
  aspect-ratio: #{$ratio-width} / #{$ratio-height};
}

.card-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  gap: rem(8px);
  aspect-ratio: #{$ratio-width} / calc((#{$ratio-height} - 12) / 2);
  color: $indigo-500;
  background: $indigo-100-light;
  border: 1px solid $indigo-100-light;
  border-radius: 4px;
  cursor: pointer;
  transition: all .2s ease-in-out;

  @include media-breakpoint-down(md) {
    font-size: rem(14px);
  }

  @media (any-hover: hover) {
    &:hover {
      border-color: $indigo-500;
    }
  }
}

.aside-wrap {
  @include media-breakpoint-up(lg) {
    border-right: 1px solid $gray-200;
    padding-right: 2px;
    height: 100%;
  }

  @include media-breakpoint-down(lg) {
    overflow-y: hidden;
    overflow-x: auto;
    margin-left: rem(-12px);
    margin-right: rem(-12px);
    margin-bottom: rem(12px);
  }
}

.aside {
  @include media-breakpoint-up(lg) {
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: rem(2px);
  }
  
  @include media-breakpoint-down(lg) {
    display: inline-flex;
    align-items: center;
    gap: rem(20px);
    padding-left: rem(12px);
    padding-right: rem(12px);
  }

  &__item {
    cursor: pointer;
    border: none;
    outline: none;
    background: none;
    color: $indigo-500;
    padding: rem(8px) 0;
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0 rem(8px);

    &.active {
      color: $gray-700;
    }

    span {
      cursor: pointer;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    &:not(.active):hover span {
      text-decoration: underline;
    }

    .item-icon {
      color: $gray-900;
      &:hover {
        color: $indigo-500;
      }
    }
  }
}

.search-cards {
  @include media-breakpoint-up(lg) {
    max-width: rem(451px);
  }
}

.grid-cards-wrap {
  min-height: calc(100vh - 303px);
}

.grid-cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(calc(20% - 24px), 1fr));
  gap: rem(20px) rem(24px);

  @include media-breakpoint-down(xl) {
    grid-template-columns: repeat(4, minmax(calc(25% - 24px), 1fr));
    gap: rem(24px);
  }

  @include media-breakpoint-down(md) {
    grid-template-columns: repeat(3, minmax(calc(33.3333333% - 12px), 1fr));
    gap: rem(12px);
  }

  @include media-breakpoint-down(sm) {
    grid-template-columns: repeat(2, minmax(calc(50% - 12px), 1fr));
  }
}

.loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 166 / 220;
  color: $indigo-500;
  
  .spinner {
    width: rem(32px);
    height: rem(32px);
  }
}

.not-results {
  background: $gray-100;
  grid-column: 2 / 6;
  border-radius: rem(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: rem(20px);
}
</style>
